import React from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import { toast } from 'react-hot-toast';
import { useHistory } from 'react-router-dom';

import { FormSubmitEvent } from 'ui/base/types';
import { ToastID, defaultToast } from 'ui/base/toast';
import { StandardPage } from 'ui/base/page/standard_page';
import { Modal, ModalStore } from 'ui/base/modal';
import { BackButton } from 'ui/base/button/back_button';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';

import { CalendarInput } from 'ui/standard/input/calendar_input';
import { ClockInput } from 'ui/standard/input/clock_input';
import { RangeInput } from 'ui/standard/input/range_input';

import { PlainButton } from 'ui/public/button';
import { NumberInput, TextInput } from 'ui/public/input';
import { ErrorMessage } from 'ui/public/error_message/error_message';

import { SeenInput } from 'ui/issues/seen_input';
import { KindInput } from 'ui/issues/kind_input';
import { DeviceInput } from 'ui/issues/device_input';

import { ResultKind } from 'services/base/types';

import { IPangolinDomain } from 'services/pangolin/types';
import { parseIssueSeverity } from 'services/pangolin/issues_service';
import { getIssueSeverityLabel } from 'services/pangolin/logic/issues/issues';

import Messages from './filter_issue_messages';
import baseStyles from './filter_issue.module.css';
import { FilterIssueStore } from './filter_issue_store';


type FilterIssueStyles = {
  modal: string;
  header: string;
  applyButton: string;
  ruleContainer: string;
  ruleTitle: string;
  labelContainer: string;
  labelRow: string;
  labelText: string;
  inputLabel: string;
  inputRow: string;
  inputItem: string;
  inputFill: string;
  inputCell: string;
  checkBoxLabel: string;
  checkBoxContainer: string;
};

const themedStyles: ThemedStyles<FilterIssueStyles> = {
  [Theme.DAYLIGHT]: {
    modal: baseStyles.modal,
    header: baseStyles.header,
    applyButton: baseStyles.daylightApplyButton,
    ruleContainer: baseStyles.daylightRuleContainer,
    ruleTitle: baseStyles.daylightRuleTitle,
    labelContainer: baseStyles.labelContainer,
    labelRow: baseStyles.labelRow,
    labelText: baseStyles.daylightLabelText,
    inputLabel: baseStyles.daylightInputLabel,
    inputRow: baseStyles.inputRow,
    inputItem: baseStyles.inputItem,
    inputFill: baseStyles.inputFill,
    inputCell: baseStyles.inputCell,
    checkBoxLabel: baseStyles.checkBoxLabel,
    checkBoxContainer: baseStyles.checkBoxContainer,
  },
  [Theme.MIDNIGHT]: {
    modal: baseStyles.modal,
    header: baseStyles.header,
    applyButton: baseStyles.midnightApplyButton,
    ruleContainer: baseStyles.midnightRuleContainer,
    ruleTitle: baseStyles.midnightRuleTitle,
    labelContainer: baseStyles.labelContainer,
    labelRow: baseStyles.labelRow,
    labelText: baseStyles.midnightLabelText,
    inputLabel: baseStyles.midnightInputLabel,
    inputRow: baseStyles.inputRow,
    inputItem: baseStyles.inputItem,
    inputFill: baseStyles.inputFill,
    inputCell: baseStyles.inputCell,
    checkBoxLabel: baseStyles.checkBoxLabel,
    checkBoxContainer: baseStyles.checkBoxContainer,
  },
};


type DetailsSectionProps = {
  store: FilterIssueStore;
};
const DetailsSection = observer(({ store }: DetailsSectionProps) => {
  const styles = useStyles(themedStyles);
  const severityMin = parseIssueSeverity(store.severityMin.value);
  const severityMinLabel = getIssueSeverityLabel(severityMin);
  const severityMax = parseIssueSeverity(store.severityMax.value);
  const severityMaxLabel = getIssueSeverityLabel(severityMax);
  return (
    <div className={styles.ruleContainer}>
      <div className={styles.labelContainer}>
        <span className={styles.inputLabel}>{Messages.searchLabel()}</span>
        <TextInput store={store.search} placeholder={Messages.searchPlaceholder()} />
      </div>
      <div className={styles.labelContainer}>
        <span className={styles.inputLabel}>{Messages.startDateLabel()}</span>
        <div className={styles.inputRow}>
          <div className={styles.inputItem}>
            <CalendarInput store={store.dateRange.first} />
          </div>
          <div className={styles.inputItem}>
            <ClockInput store={store.timeStart}/>
          </div>
        </div>
      </div>
      <div className={styles.labelContainer}>
        <span className={styles.inputLabel}>{Messages.endDateLabel()}</span>
        <div className={styles.inputRow}>
          <div className={styles.inputItem}>
            <CalendarInput store={store.dateRange.final} />
          </div>
          <div className={styles.inputItem}>
            <ClockInput store={store.timeEnd}/>
          </div>
        </div>
      </div>
      <div className={styles.labelContainer}>
        <span className={styles.inputLabel}>{Messages.seenLabel()}</span>
        <SeenInput store={store.seen} placeholder={Messages.seenPlaceholder()}/>
      </div>
      <ErrorMessage message={store.seenError}/>
      <div className={styles.labelContainer}>
        <span className={styles.inputLabel}>{Messages.severityLabel()}</span>
        <div className={styles.inputRow}>
          <div className={styles.inputFill}>
            <RangeInput store={store.severityMin}
                        base={1} ceil={5} min={1}
                        max={store.severityMax.value}
                        step={1} invert={true} />
            <div className={styles.labelRow}>
              <div className={styles.labelText}>
                {Messages.rangeMinLabel()}
              </div>
              <div className={styles.labelText}>
                {severityMinLabel.toUpperCase()} ({store.severityMin.value.toFixed(1)})
              </div>
            </div>
          </div>
        </div>
        <div className={styles.inputRow}>
          <div className={styles.inputFill}>
            <RangeInput store={store.severityMax}
                        base={1} ceil={5} max={5}
                        min={store.severityMin.value}
                        step={1} invert={false} />
            <div className={styles.labelRow}>
              <div className={styles.labelText}>
                {Messages.rangeMaxLabel()}
              </div>
              <div className={styles.labelText}>
                {severityMaxLabel.toUpperCase()} ({store.severityMax.value.toFixed(1)})
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.labelContainer}>
        <span className={styles.inputLabel}>{Messages.affectedLabel()}</span>
        <div className={styles.inputRow}>
          <div className={styles.inputCell}>
            <NumberInput store={store.affectedMin}
                         max={store.affectedMax.value}
                         min={0} step={1}
                         placeholder='--' />
          </div>
          &mdash;
          <div className={styles.inputCell}>
            <NumberInput store={store.affectedMax}
                         min={isNaN(store.affectedMin.value) ? 0 :
                              store.affectedMin.value}
                         max={NaN} step={1}
                         placeholder='--' />
          </div>
        </div>
        <div className={styles.labelRow}>
          <div className={styles.labelText}>
            {Messages.rangeMinLabel()}
          </div>
          <div className={styles.labelText}>
            {Messages.rangeMaxLabel()}
          </div>
        </div>
        <ErrorMessage message={store.affectedError}/>
      </div>
      <div className={styles.labelContainer}>
        <span className={styles.inputLabel}>{Messages.incidentsLabel()}</span>
        <div className={styles.inputRow}>
          <div className={styles.inputCell}>
            <NumberInput store={store.incidentsMin}
                         max={store.incidentsMax.value}
                         min={0} step={1}
                         placeholder='--' />
          </div>
          &mdash;
          <div className={styles.inputCell}>
            <NumberInput store={store.incidentsMax}
                         min={isNaN(store.incidentsMin.value) ? 0 :
                              store.incidentsMin.value}
                         max={NaN} step={1}
                         placeholder='--' />
          </div>
        </div>
        <div className={styles.labelRow}>
          <div className={styles.labelText}>
            {Messages.rangeMinLabel()}
          </div>
          <div className={styles.labelText}>
            {Messages.rangeMaxLabel()}
          </div>
        </div>
        <ErrorMessage message={store.incidentsError}/>
      </div>
    </div>
  );
});

type KindsSectionProps = {
  store: FilterIssueStore;
};
const KindsSection = observer(({ store }: KindsSectionProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.ruleContainer}>
      <div className={styles.ruleTitle}>{Messages.kindsLabel()}</div>
      <KindInput store={store.kinds} placeholder={Messages.kindsPlaceholder()}/>
    </div>
  );
});

type DevicesSectionProps = {
  domain: IPangolinDomain;
  store: FilterIssueStore;
};
const DevicesSection = observer(({ domain, store }: DevicesSectionProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.ruleContainer}>
      <div className={styles.ruleTitle}>{Messages.devicesLabel()}</div>
      <DeviceInput store={store.devices} hosts={domain.hosts} placeholder={Messages.devicesPlaceholder()}/>
    </div>
  );
});

type FilterIssueMainProps = {
  domain: IPangolinDomain;
  store: FilterIssueStore;
};
export const FilterIssueMain = observer(({ domain, store }: FilterIssueMainProps) => {
  const history = useHistory();
  const styles = useStyles(themedStyles);

  const onHideModal = React.useCallback(action(() => {
    history.goBack();
  }), []);

  const onCommit = React.useCallback(action((event: FormSubmitEvent) => {
    event.preventDefault();
    if (store != null) {
      const result = store.handleApply(domain);
      if (result.kind === ResultKind.OK) {
        toast.dismiss(ToastID.ERROR_ACTION_GENERIC);
        history.goBack();
        return;
      } else {
        defaultToast(ToastID.ERROR_ACTION_GENERIC);
      }
    }
  }), [store, domain]);

  return (
    <StandardPage>
      <form onSubmit={onCommit} noValidate>
        <div className={styles.header}>
          <BackButton onClick={onHideModal}/>
          <PlainButton type='submit'
            className={styles.applyButton}>{Messages.apply()}
          </PlainButton>
        </div>
        <DetailsSection store={store}/>
        <KindsSection store={store} />
        <DevicesSection store={store} domain={domain} />
      </form>
    </StandardPage>
  );
});

type BaseFilterIssueProps = {
  domain: IPangolinDomain;
  modal: ModalStore;
};
export const BaseFilterIssue = observer(({ domain, modal }: BaseFilterIssueProps) => {
  const hosts = domain.hosts;
  const filter = domain.issuesFilter;

  const visible = modal.visible;

  const [store, setStore] = React.useState(() => (
    visible ? new FilterIssueStore(filter, hosts) : null
  ));

  React.useEffect(() => {
    if (visible && store === null) {
      setStore(new FilterIssueStore(filter, hosts));
    } else if (!visible && store !== null) {
      store.clean(); setStore(null);
    }

    return () => {
      if (store !== null) {
        store.clean();
      }
    };
  }, [store, visible, filter, hosts]);

  return store == null ? (
    <StandardPage>
    </StandardPage>
  ) : (
    <FilterIssueMain
      domain={domain}
      store={store}
    />
  );
});

type FilterIssueModalProps = {
  domain: IPangolinDomain;
  modal: ModalStore;
};
export const FilterIssueModal = observer(({ domain, modal }: FilterIssueModalProps) => {
  const styles = useStyles(themedStyles);
  return (
    <Modal store={modal}>
      <div className={styles.modal}>
        <BaseFilterIssue
          domain={domain}
          modal={modal}
        />
      </div>
    </Modal>
  );
});
